/**
 * ASN main-tab wrapper (Purchasing → ASN).
 *
 * Renders two sub-tabs:
 *   - "Delivery Lines" (default): selectable released PO lines with a
 *     Create-ASN flow ({@link DeliveryLinesTab}).
 *   - "ASN": the full Advanced Shipping Notice list ({@link ASNListTab}).
 *
 * When an ASN is created from the Delivery Lines tab, the user is switched
 * to the ASN sub-tab and the list is refreshed via an imperative handle.
 */
import { useEffect, useRef, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { SectionTourTrigger } from '../../tour/SectionTourTrigger';
import { useIsCustomer } from '../../../hooks/usePortalMode';
import DeliveryLinesTab from './DeliveryLinesTab';
import ASNListTab, { ASNListTabHandle } from './ASNListTab';

type ASNSubTab = 'delivery_lines' | 'asn';

export default function ASNTab() {
  // Customers only see ASNs — Delivery Lines (the supplier's Create-ASN
  // staging view) is hidden for them. Dual-mode supplier sites honour the
  // customer/supplier toggle via useIsCustomer().
  const isCustomer = useIsCustomer();
  const [subTab, setSubTab] = useState<ASNSubTab>(isCustomer ? 'asn' : 'delivery_lines');
  const asnListRef = useRef<ASNListTabHandle>(null);
  // Set when an ASN is created so we refresh the list *after* the tab switch
  // has committed (and after the ASN list's own initial load has completed),
  // rather than firing refresh() synchronously into a not-yet-ready child.
  const [refreshToken, setRefreshToken] = useState(0);

  // After creating an ASN from selected delivery lines, switch to the ASN
  // sub-tab and request a refresh of its list so the new ASN is visible
  // immediately without a manual reload.
  const handleASNCreated = () => {
    setSubTab('asn');
    setRefreshToken((t) => t + 1);
  };

  // Fire the refresh once the switch to the ASN sub-tab has committed. The
  // effect runs after render/commit, so the ASN list (always mounted) has a
  // valid imperative handle and its initial load has had a chance to finish.
  useEffect(() => {
    if (refreshToken === 0) return;
    asnListRef.current?.refresh();
  }, [refreshToken]);

  // If auth resolves to a customer after mount, leave the (now-hidden)
  // Delivery Lines tab.
  useEffect(() => {
    if (isCustomer && subTab === 'delivery_lines') setSubTab('asn');
  }, [isCustomer, subTab]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Tabs value={subTab} onValueChange={(v) => setSubTab(v as ASNSubTab)}>
          <TabsList>
            {!isCustomer && <TabsTrigger value="delivery_lines">Delivery Lines</TabsTrigger>}
            <TabsTrigger value="asn">ASN</TabsTrigger>
          </TabsList>
        </Tabs>
        <SectionTourTrigger
          pageKey="purchasing"
          sectionKey="asn"
          variant="link"
          label="Tour this tab"
        />
      </div>

      {/* Keep both mounted so the ASN list keeps its loaded state and the
          imperative refresh handle stays valid across sub-tab switches. */}
      {!isCustomer && (
        <div className={subTab === 'delivery_lines' ? '' : 'hidden'}>
          <DeliveryLinesTab onASNCreated={handleASNCreated} />
        </div>
      )}
      <div className={subTab === 'asn' ? '' : 'hidden'}>
        <ASNListTab ref={asnListRef} />
      </div>
    </div>
  );
}
